import { Box, SxProps, Theme } from "@mui/material";
import type { JSX } from "react";
import { ReactNode } from "react";

type NotificationPanelType = "info" | "warning" | "error";

interface NotificationPanelProps {
  type: NotificationPanelType;
  icon?: JSX.Element;
  header?: string;
  text?: string;
  children?: ReactNode;
  contentSx?: SxProps<Theme>;
}

export const NotificationPanel = ({
  type,
  icon,
  header,
  text,
  children,
  contentSx,
}: NotificationPanelProps): JSX.Element => {
  return (
    <Box
      sx={(theme) => {
        const notification = theme.semanticColors.notification[type];

        return {
          border: "1px solid transparent",
          borderRadius: "4px",
          bgcolor: notification.background,
          width: "100%",
          backgroundImage: `
            linear-gradient(to right, ${notification.border} 0 4px, transparent 0 8px),
            linear-gradient(to right, ${notification.border} 0 4px, transparent 0 8px),
            linear-gradient(to bottom, ${notification.border} 0 4px, transparent 0 8px),
            linear-gradient(to bottom, ${notification.border} 0 4px, transparent 0 8px)
          `,
          backgroundRepeat: "repeat-x, repeat-x, repeat-y, repeat-y",
          backgroundSize: "8px 1px, 8px 1px, 1px 8px, 1px 8px",
          backgroundPosition: "0 0, 0 100%, 0 0, 100% 0",
          backgroundOrigin: "border-box",
          backgroundClip: "border-box",
        };
      }}
    >
      <Box
        sx={[
          { display: "flex", gap: "12px", p: "19px", flexDirection: "row", alignItems: "center" },
          ...(Array.isArray(contentSx) ? contentSx : [contentSx].filter(Boolean)),
        ]}
      >
        {icon}
        {(header || text || children) && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              color: (theme) => theme.semanticColors.notification[type].text,
              fontSize: "13px",
              lineHeight: 1.3,
            }}
          >
            {header && <Box sx={{ fontWeight: "bold" }}>{header}</Box>}
            {text && <Box>{text}</Box>}
            <Box>{children}</Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};
